import { Controller } from "@hotwired/stimulus"

// Pulls a product image (og:image) from a pasted shop link and shows it on the
// wish. Debounces the link input, POSTs the URL to /link_preview, and on success
// fills the hidden photo_url field + shows a thumbnail. Every failure path clears
// quietly — the system-test harness fails on any console error, so this never
// throws.
//
//   div{data: {controller: "link-preview"}}
//     input{data: {link_preview_target: "input", action: "input->link-preview#changed"}}
//     input[hidden]{data: {link_preview_target: "photo"}}        (nested photo_url)
//     div{data: {link_preview_target: "preview"}}
//       div{data: {link_preview_target: "spinner"}} …
//       div{data: {link_preview_target: "result"}}
//         img{data: {link_preview_target: "image"}}
//         button{data: {action: "link-preview#remove"}}
export default class extends Controller {
  static targets = ["input", "photo", "preview", "spinner", "result", "image"]
  static values = { url: { type: String, default: "/link_preview" } }

  DEBOUNCE = 600

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
  }

  changed() {
    if (this.timer) clearTimeout(this.timer)
    const value = this.inputTarget.value.trim()
    if (!this.looksLikeUrl(value)) {
      this.clear()
      return
    }
    this.timer = setTimeout(() => this.load(value), this.DEBOUNCE)
  }

  async load(raw) {
    const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
    this.showLoading()
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ url })
      })
      if (!response.ok) return this.clear()
      const data = await response.json()
      if (data && data.image_url) this.show(data.image_url)
      else this.clear()
    } catch (_error) {
      this.clear()
    }
  }

  remove(event) {
    if (event) event.preventDefault()
    this.clear()
  }

  // --- view states --------------------------------------------------------
  showLoading() {
    if (this.hasPreviewTarget) this.previewTarget.hidden = false
    if (this.hasSpinnerTarget) this.spinnerTarget.hidden = false
    if (this.hasResultTarget) this.resultTarget.hidden = true
  }

  show(imageUrl) {
    if (this.hasPhotoTarget) this.photoTarget.value = imageUrl
    if (this.hasImageTarget) this.imageTarget.src = imageUrl
    if (this.hasPreviewTarget) this.previewTarget.hidden = false
    if (this.hasSpinnerTarget) this.spinnerTarget.hidden = true
    if (this.hasResultTarget) this.resultTarget.hidden = false
  }

  clear() {
    if (this.hasPhotoTarget) this.photoTarget.value = ""
    if (this.hasImageTarget) this.imageTarget.removeAttribute("src")
    if (this.hasPreviewTarget) this.previewTarget.hidden = true
    if (this.hasSpinnerTarget) this.spinnerTarget.hidden = true
    if (this.hasResultTarget) this.resultTarget.hidden = true
  }

  // --- helpers ------------------------------------------------------------
  looksLikeUrl(value) {
    return /^https?:\/\//i.test(value) || /\.[a-z]{2,}/i.test(value)
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
